import { useRef, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import classnamesBind from "classnames/bind";

import styles from "./ProductTile.scss";
import SaveForLaterButton from "../buttons/saveForLaterButton/SaveForLaterButton";
import ProductSponsorLabel from "../productSponsorLabel/ProductSponsorLabel";
import CircleButton from "../buttons/circle/CircleButton";
import { CAROUSEL_IMAGE_SIZE, PAGE_AREAS } from "../../constants";
import Image from "../image/Image";
import saveForLater from "../../utils/saveForLater";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { useFeaturesReady } from "../../context/featuresReady/FeaturesReadyContext";
import { getFeature } from "../../utils/features";
import { useLooksProducts } from "../../context/looks/LooksProductsContext";
import {
  callOnViewBeacon,
  callOnLoadBeacon,
  clearCallOnViewBeacon,
} from "../../../common/CriteoAdvertsForRecs";

const classnames = classnamesBind.bind(styles);

const { MIGHT_LIKE, BUY_THE_LOOK, RECENTLY_VIEWED, OUT_OF_STOCK, MAIN } =
  PAGE_AREAS;

export default function ProductTile({
  productId,
  url,
  imageUrl,
  title,
  colourWayId,
  isMixAndMatch,
  removeFunction,
  screenHoverable,
  pageArea,
  shouldShowTitle,
  saveForLaterClass,
  children,
  productTileAriaLabel,
  advertisement,
  showAdvertElements,
}) {
  const variantIdentifier = `colourWayId:${colourWayId}`;
  const blurTimer = useRef(null);
  const targetAdvertRef = useRef();
  const [isRefReady, setIsRefReady] = useState(false);
  const [shouldShowRemoveButton, setShouldShowRemoveButton] = useState(false);
  const { getTranslation } = useRuntimeConstants();
  const { areFeaturesReady } = useFeaturesReady();
  const { looksProducts } = useLooksProducts();

  const showRemoveButton = () => {
    clearTimeout(blurTimer.current);
    setShouldShowRemoveButton(true);
  };

  const hideRemoveButton = () => {
    blurTimer.current = setTimeout(() => setShouldShowRemoveButton(false));
  };

  const handleRemoveClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    removeFunction(productId, title);
  };

  const handleSaveClick = (event) => {
    event.stopPropagation();
    event.preventDefault();

    saveForLater({
      product: { id: productId },
      items: [{ productId, colourWayId }],
      pageArea,
      looksProducts,
    });
  };

  const handleCriteoCall = () => {
    if (!advertisement) return;
    navigator?.sendBeacon?.(advertisement.OnClickBeacon);
  };

  const onViewBeaconReady = useCallback(
    (node) => {
      if (node) {
        targetAdvertRef.current = node;
        if (!isRefReady) {
          setIsRefReady(true);
        }
      }
    },
    [isRefReady]
  );

  useEffect(() => {
    if (!advertisement || !isRefReady) {
      return;
    }

    callOnViewBeacon(targetAdvertRef, advertisement.OnViewBeacon);
    callOnLoadBeacon(advertisement.OnLoadBeacon);

    if (advertisement.containerBeacons) {
      callOnViewBeacon(
        targetAdvertRef,
        advertisement.containerBeacons.OnViewBeacon
      );
      callOnLoadBeacon(advertisement.containerBeacons.OnLoadBeacon);
    }

    return () => {
      clearCallOnViewBeacon(targetAdvertRef);
    };
  }, [advertisement, isRefReady]);

  const renderImage = () => (
    <div className={styles.skeleton} ref={onViewBeaconReady}>
      <Image
        additionalClasses={styles.image}
        ariaHidden={shouldShowTitle}
        url={imageUrl}
        alternateText={title}
        type={"carousel"}
        width={CAROUSEL_IMAGE_SIZE}
        loadingType={"lazy"}
      />
      {showAdvertElements && (
        <ProductSponsorLabel
          labelText={
            advertisement ? getTranslation("sponsored_product_ads") : ""
          }
        />
      )}
    </div>
  );

  const renderRemoveItemRecentlyViewed = () => (
    <>
      {removeFunction && (shouldShowRemoveButton || !screenHoverable) && (
        <CircleButton
          handleClick={handleRemoveClick}
          additionalClasses={styles.closeButton}
          iconClass={classnames(styles.closeIcon, "product-closesmall")}
          ariaLabel={getTranslation("pdp_carousel_recently_viewed_remove_item")}
          testId="removeProduct"
        />
      )}
    </>
  );

  const createLinkButtonElement = () => (
    <>
      <a
        href={url}
        className={styles.link}
        aria-label={productTileAriaLabel}
        onClick={handleCriteoCall}
      >
        {renderImage()}
        {children}
      </a>
      {renderRemoveItemRecentlyViewed()}
      {renderSaveForLater()}
    </>
  );

  const showSFL = () =>
    pageArea !== MIGHT_LIKE ||
    (areFeaturesReady && getFeature("pdp-sfl-ymal").isEnabled());

  const renderSaveForLater = () => {
    if (!isMixAndMatch && showSFL()) {
      return (
        <SaveForLaterButton
          handleClick={handleSaveClick}
          productTitle={title}
          variantIdentifier={variantIdentifier}
          saveForLaterClass={saveForLaterClass}
        />
      );
    }
  };

  return (
    <div
      className={styles.container}
      role="group"
      onMouseEnter={showRemoveButton}
      onMouseLeave={hideRemoveButton}
      onFocus={showRemoveButton}
      onBlur={hideRemoveButton}
      aria-label={getTranslation("generic_accessibility_product")}
    >
      {createLinkButtonElement()}
    </div>
  );
}

ProductTile.propTypes = {
  productId: PropTypes.number.isRequired,
  url: PropTypes.string.isRequired,
  imageUrl: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  colourWayId: PropTypes.number.isRequired,
  isMixAndMatch: PropTypes.bool,
  removeFunction: PropTypes.func,
  screenHoverable: PropTypes.bool,
  pageArea: PropTypes.oneOf([
    MIGHT_LIKE,
    BUY_THE_LOOK,
    RECENTLY_VIEWED,
    OUT_OF_STOCK,
    MAIN,
  ]),
  shouldShowTitle: PropTypes.bool,
  saveForLaterClass: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  productTileAriaLabel: PropTypes.string.isRequired,
  advertisement: PropTypes.object,
  showAdvertElements: PropTypes.bool,
};
